import { createSlice, PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { IModule } from 'redux-dynamic-modules';
import {
  checkSignUpEmail,
  createPassword,
  logInWithPassword,
  createSignInWithEmailLink,
} from './loginAsyncActions';
import redirectMiddleware from './redirectMiddleware';

type State = {
  isLoading: boolean;
  error?: SerializedError;
  email?: string;
  returnUrl?: string;
  allowUnverifiedEmail?: boolean;
  step: Step;
  providers?: string[];
  sentEmailLink: boolean;
};

export type Step = typeof ALL_STEPS[number];
const ALL_STEPS = ['login', 'sign-up-email', 'create-password'] as const;
export const isStep = (step: string): step is Step => ALL_STEPS.includes(step as Step);

const initialState: State = {
  isLoading: false,
  step: 'login',
  sentEmailLink: false,
};

const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    setEmail: (state, { payload }: PayloadAction<Pick<State, 'email'>>) => {
      state.email = payload.email;
    },
    setReturnUrl: (state, { payload }: PayloadAction<Pick<State, 'returnUrl'>>) => {
      state.returnUrl = payload.returnUrl;
    },
    setAllowUnverifiedEmail: (
      state,
      { payload }: PayloadAction<Pick<State, 'allowUnverifiedEmail'>>,
    ) => {
      state.allowUnverifiedEmail = payload.allowUnverifiedEmail;
    },
    setDefaultStep: (state) => {
      state.step = initialState.step;
    },
    setStep: (state, { payload }: PayloadAction<Pick<State, 'step'>>) => {
      state.step = payload.step;
    },
    setError: (state, { payload }: PayloadAction<Pick<State, 'error'>>) => {
      state.error = payload.error;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      logInWithPassword.pending,
      (
        state,
        {
          meta: {
            arg: { email },
          },
        },
      ) => {
        state.email = email;
        state.isLoading = true;
        state.error = undefined;
      },
    );
    builder.addCase(logInWithPassword.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(logInWithPassword.rejected, (state, { error }) => {
      state.isLoading = false;
      state.error = error;
    });
    builder.addCase(
      createSignInWithEmailLink.pending,
      (
        state,
        {
          meta: {
            arg: { email },
          },
        },
      ) => {
        state.email = email;
        state.isLoading = true;
        state.error = undefined;
      },
    );
    builder.addCase(createSignInWithEmailLink.fulfilled, (state) => {
      state.isLoading = false;
      state.sentEmailLink = true;
    });
    builder.addCase(createSignInWithEmailLink.rejected, (state, { error }) => {
      state.isLoading = false;
      state.error = error;
      state.sentEmailLink = false;
    });
    builder.addCase(
      checkSignUpEmail.pending,
      (
        state,
        {
          meta: {
            arg: { email },
          },
        },
      ) => {
        state.email = email;
        state.isLoading = true;
        state.error = undefined;
        state.providers = undefined;
      },
    );
    builder.addCase(
      checkSignUpEmail.fulfilled,
      (state, { payload: { providers, step, error } }) => {
        state.isLoading = false;
        state.error = error;
        state.providers = providers;
        if (step) {
          state.step = step;
        }
      },
    );
    builder.addCase(checkSignUpEmail.rejected, (state, { error }) => {
      state.isLoading = false;
      state.error = error;
    });
    builder.addCase(createPassword.pending, (state) => {
      state.isLoading = true;
      state.error = undefined;
    });
    builder.addCase(createPassword.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(createPassword.rejected, (state, { error }) => {
      state.isLoading = false;
      state.error = error;
    });
  },
});

export const { reducer, actions } = loginSlice;

export type LoginModuleState = {
  login: State;
};

export const getLoginModule = (): IModule<LoginModuleState> => {
  return {
    id: 'login',
    reducerMap: {
      login: reducer,
    },
    middlewares: [redirectMiddleware],
    retained: false,
  };
};
